import { Grid } from "@mui/material";
import { FieldArrayWithId, useFieldArray, useFormContext } from "react-hook-form";
import { useState } from "react";
import { RhfControlledTableColumnDefinition } from "../../../Layout/RhfControlledTable/RhfControlledTableColumnDefinition";
import { defaultSupplierHandlingTime, SupplierHandlingTime } from "../../SupplierHandlingTime";
import { RhfControlledDataTable } from "../../../Layout/RhfControlledTable/RhfControlledDataTable";
import Odc from "../../Odc";
import { isArrayUndefinedOrEmpty, mapKeyToArray } from "../../../Utils/arrays";
import { SupplierHandlingTimeFilterType } from "./HandlingTimeFilterType";
import { RhfHandlingTimeTextField } from "./RhfHandlingTimeTextField";
import { mapReactHookFormIndizesToGivenFieldArray } from "../../../Utils/reactHookFormUtils";
import { supplierUrls } from "../../../URL/ApplicationUrls";
import { RHFAutoCompleteGsdbSelectionSingleWithDebouncedFetch } from "../../../Supplier/Form/RHFAutoCompleteGsdbSelectionSingleWithDebouncedFetch";
import { useFetchGeoLocationSelectionItems } from "../../../Locations/useFetchGeoLocationSelectionItems";
import { distinctStringItems } from "../../../Utils/strings";
import { RhfControlledDataTableFilter } from "../../../Layout/RhfControlledTable/RhfControlledDataTableFilter";
import { ResetFilters } from "../../../Layout/RhfControlledTable/ResetFilters";

export const SupplierHandlingTimesDataTable = () => {
  const {
    control,
    getValues,
    formState: { disabled: isFormDisabled },
  } = useFormContext<Odc>();
  const { fields, prepend, remove } = useFieldArray<Odc>({
    control,
    name: "SupplierHandlingTimes",
  });

  const [filteredFields, setFilteredFields] =
    useState<
      FieldArrayWithId<Odc, "CountryHandlingTimes" | "SupplierHandlingTimes" | "SprHandlingTimes">[]
    >();

  const filterSupplierHandlingTimes = (
    fieldArray: FieldArrayWithId<
      Odc,
      "CountryHandlingTimes" | "SupplierHandlingTimes" | "SprHandlingTimes"
    >[],
    propertyToFilterFor: SupplierHandlingTimeFilterType,
    searchValue: string
  ) => {
    const foundFields = fieldArray.filter((field) => {
      const supplierHandlingTimeField = field as SupplierHandlingTime;

      if (propertyToFilterFor === "SupplierGsdb")
        return supplierHandlingTimeField.SupplierGsdb.startsWith(searchValue);

      if (propertyToFilterFor === "HandlingTime")
        return supplierHandlingTimeField.HandlingTime.startsWith(searchValue);

      return [];
    });

    if (isArrayUndefinedOrEmpty(foundFields)) {
      setFilteredFields(undefined);
      return;
    }

    const filteredFieldsWithIndexRelationToRhfFieldArray = mapReactHookFormIndizesToGivenFieldArray(
      {
        fieldsFromFormContext: fields,
        filteredFields: foundFields,
      }
    );

    setFilteredFields(filteredFieldsWithIndexRelationToRhfFieldArray);
  };

  const supplierHandlingTimes = getValues("SupplierHandlingTimes");
  const odcId = getValues("Id");

  const { data: initialSupplierItems } = useFetchGeoLocationSelectionItems({
    apiUrl: supplierUrls.initialSelectableGsdbs.apiUrl,
    selectedItems: distinctStringItems(mapKeyToArray(supplierHandlingTimes, "SupplierGsdb")),
    errorMessage: "Supplier",
    queryKeyName: "supplierHandlingTime",
    queryKeySubName: "suppliers",
    id: odcId,
  });

  const supplierHandlingTimesTableColumns: RhfControlledTableColumnDefinition<
    Odc,
    "SupplierHandlingTimes"
  >[] = [
    {
      fieldKey: "SupplierGsdb",
      isNumeric: false,
      headerText: "Supplier",
      width: "50%",
      render: (fieldName, disabled) => (
        <RHFAutoCompleteGsdbSelectionSingleWithDebouncedFetch
          control={control}
          name={fieldName}
          initialSelectionItems={initialSupplierItems || []}
          apiUrl={supplierUrls.selectableGsdbsNew.apiUrl}
          errorMessage="An error occurred when retrieving the Supplier GSDBs"
          disabled={isFormDisabled || disabled}
        />
      ),
    },
    {
      fieldKey: "HandlingTime",
      isNumeric: false,
      headerText: "Handling Time",
      width: "50%",
      render: (fieldName, disabled) => (
        <RhfHandlingTimeTextField
          name={fieldName}
          control={control}
          disabled={isFormDisabled || disabled}
        />
      ),
    },
  ];

  const [resetFilters, setResetFilters] = useState<boolean>(false);
  return (
    <>
      <Grid container direction="row" pl={2} mt={1} spacing={0.5}>
        <Grid item>
          <RhfControlledDataTableFilter
            reset={resetFilters}
            placeholder="Supplier GSDB"
            handleSearch={(searchValue: string) =>
              filterSupplierHandlingTimes(fields, "SupplierGsdb", searchValue)
            }
            disabled={isArrayUndefinedOrEmpty(initialSupplierItems)}
          />
        </Grid>
        <Grid item>
          <RhfControlledDataTableFilter
            reset={resetFilters}
            placeholder="Handling Time"
            handleSearch={(searchValue: string) =>
              filterSupplierHandlingTimes(fields, "HandlingTime", searchValue)
            }
            disabled={isArrayUndefinedOrEmpty(initialSupplierItems)}
          />
        </Grid>
        <ResetFilters
          handleClick={() => {
            setFilteredFields(undefined);
            setResetFilters(!resetFilters);
          }}
        />
      </Grid>
      <Grid item xs={12} container direction="column" style={{ paddingTop: "4px" }}>
        <RhfControlledDataTable
          fieldArrayName="SupplierHandlingTimes"
          columns={supplierHandlingTimesTableColumns}
          defaultForNewRow={defaultSupplierHandlingTime}
          rows={filteredFields ?? fields}
          insertRow={prepend}
          removeRow={remove}
        />
      </Grid>
    </>
  );
};
